import WCheckbox from "../wcheckbox/WCheckbox";
import WTextInput from "../wtext-input/WTextInput";
import WButton from "../wbutton/WButton";
import { SubmitHandler, useForm } from "react-hook-form";
import { motion } from "framer-motion";
import {
  useCheckUsernameQuery,
  useMeQuery,
  UsersPermissionsLoginInput,
  UsersPermissionsUserInput,
  UsersPermissionsUserQuery,
  useUpdateUsersPermissionsUserMutation,
} from "@/generated";
import { WPasswordStrength } from "../wpassword-strength/WPasswordStrength";
import { ApolloError } from "@apollo/client";
import WDataWrapper from "../wdata-wrapper/WDataWrapper";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import routes from "@/utils/routes";
import { useRouter } from "next/router";
import WPhoneInputWrapper from "../wphone-input-wrapper/WPhoneInputWrapper";
import Datepicker from "react-tailwindcss-datepicker";
import moment from "moment";
import parse from "html-react-parser";
import clsx from "clsx";
import WTypography from "../wtypography/WTypography";

type AuthProvider = {
  id: string;
  name: string;
  signIn: () => void;
};

interface IProps {
  onComplete?: (data?: IFormInputs) => void;
  onCancel?: () => void;
  profile?:
    | (NonNullable<
        NonNullable<UsersPermissionsUserQuery["usersPermissionsUser"]>["data"]
      >["attributes"] & { id?: string })
    | null
    | undefined;
  loading: boolean;
  error?: ApolloError | undefined;
  isProfileEdit?: boolean;
  withoutUsername?: boolean;
  validateExistingEmail?: boolean;
  title?: string;
  description?: string;
  withoutPasswordCheck?: boolean;
  isWebinarSignup?: boolean;
  onLogin?: (inputVariables: UsersPermissionsLoginInput) => void;
  providers?: AuthProvider[];
  socialLinksVisible?: boolean;
  buttonText?: string;
  callback?: () => void;
  validateSubscriptionPrize?: boolean;
}

interface IFormInputs {
  username: string;
  email: string;
  password: string;
  passwordCheck: string;
  firstName: string;
  lastName: string;
  schoolGrade?: string;
  phone?: string;
  role?: string;
  birthday?: string;
  city?: string;
  school?: string;
}

interface ISpecificValues {
  firstLastName: boolean;
  password: boolean;
  birthday: boolean;
  city: boolean;
  phone: boolean;
  checkbox: boolean;
  submitButton: boolean;
}

export const WProfileEditPersonalDataForm = ({
  onCancel,
  onComplete,
  profile,
  loading,
  error,
  isProfileEdit,
  validateExistingEmail,
  withoutUsername,
  title,
  description,
  isWebinarSignup,
  onLogin,
  buttonText,
  callback,
  validateSubscriptionPrize,
}: IProps) => {
  const {
    register,
    getValues,
    control,
    watch,
    formState: { isValid, errors },
    handleSubmit,
    setValue,
    clearErrors,
    reset,
  } = useForm<IFormInputs>({
    mode: "all",
    defaultValues: {
      firstName: profile?.firstName || "",
      lastName: profile?.lastName || "",
      username: profile?.username,
      email: profile?.email,
    },
  });
  const watchRole = watch("role", "1");
  const watchPassword = watch("password", "");
  const [emailVerification, setEmailVerification] = useState(String);
  const birthday = watch("birthday");

  const [hideInput, setHideInput] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const [showSpecificValues, setShowSpecificValues] = useState<ISpecificValues>(
    {
      firstLastName: !!isProfileEdit,
      password: !!isProfileEdit,
      birthday: !!isProfileEdit,
      city: !!isProfileEdit,
      phone: !!isProfileEdit,
      checkbox: !!isProfileEdit,
      submitButton: true,
    }
  );

  const resetSpecificValues = useCallback(() => {
    setShowSpecificValues({
      firstLastName: !!isProfileEdit,
      password: !!isProfileEdit,
      birthday: !!isProfileEdit,
      city: !!isProfileEdit,
      phone: !!isProfileEdit,
      checkbox: !!isProfileEdit,
      submitButton: true,
    });
    setHideInput(true);
    setShowPassword(false);

    const valuesToReset = ["firstName", "lastName", "password", "birthday"];

    valuesToReset?.forEach((item) => {
      if (getValues(item as keyof IFormInputs) !== "") {
        setValue(item as keyof IFormInputs, "");
      }
    });
  }, [getValues, isProfileEdit, setValue]);

  useEffect(() => {
    if (isWebinarSignup) {
      setHideInput(true);
    }
  }, [clearErrors, isWebinarSignup]);

  const { t } = useTranslation("common");
  const { t: tProfileEdit } = useTranslation("profileEdit");

  const [updateProfile, updateProfileResponse] =
    useUpdateUsersPermissionsUserMutation();

  const { data: meData } = useMeQuery({
    fetchPolicy: "network-only",
  });
  const router = useRouter();

  const { data: userData } = useCheckUsernameQuery({
    fetchPolicy: "network-only",
    variables: {
      filters: {
        email: {
          eq: emailVerification,
        },
      },
    },
  });

  const userId = meData?.me?.id?.toString();

  const validateEmail = useCallback((email: string) => {
    if (email && /^[\w-.+]+@([\w-]+\.)+[\w-]{2,4}$/.test(email)) {
      setEmailVerification(email);
    } else {
      setEmailVerification("");
    }
  }, []);

  const toggleFieldValue = useCallback((name: string, value: boolean) => {
    setShowSpecificValues((prevValues) => ({
      ...prevValues,
      [name]: value,
    }));
  }, []);

  const onSubmit: SubmitHandler<IFormInputs> = useCallback(
    async (data) => {
      if (isWebinarSignup && hideInput && !showPassword) {
        if (data?.email) {
          if (userData?.usersPermissionsUsers?.data?.length !== 0) {
            setShowPassword(true);
            setHideInput(false);
          } else {
            // Continue the registration flow
            setHideInput(false);
            toggleFieldValue("firstLastName", true);
            toggleFieldValue("submitButton", false);
          }
        }

        return;
      }

      if (isWebinarSignup && showPassword && onLogin) {
        onLogin({
          identifier: data?.email,
          password: data?.password,
          provider: "local",
        });

        callback && callback();

        return;
      }

      if (userId) {
        let dataUser: UsersPermissionsUserInput = {
          role: data?.role,
          firstName: data?.firstName?.trim(),
          lastName: data?.lastName?.trim(),
          birthday: moment(data?.birthday, "YYYY-M-D")?.format("YYYY-MM-DD"),
          city: data?.city?.trim(),
          phone: data?.phone,
        };

        if (data?.username) {
          dataUser = {
            ...dataUser,
            username: data?.username?.trim(),
          };
        }

        updateProfile({
          onCompleted: () => {
            if (onComplete) {
              onComplete();
              return;
            }
            reset();
            toast.success(t("saved-profile"));
          },
          onError: () => toast.error(t("error-occurred")),
          variables: {
            updateUsersPermissionsUserId: userId,
            data: dataUser,
          },
        }).then(() => {
          if (onCancel) {
            onCancel();
          }
        });
      } else if (!isProfileEdit) {
        data = {
          ...data,
          birthday: moment(data?.birthday, "YYYY-M-D")?.format("YYYY-MM-DD"),
        };
        onComplete && onComplete(data);
      }
    },
    [
      isWebinarSignup,
      hideInput,
      showPassword,
      onLogin,
      userId,
      isProfileEdit,
      userData?.usersPermissionsUsers?.data?.length,
      toggleFieldValue,
      callback,
      updateProfile,
      onComplete,
      reset,
      t,
      onCancel,
    ]
  );

  const today = useMemo(() => new Date(), []);
  const maxDate = useMemo(
    () => new Date(today.getFullYear() - 10, today.getMonth(), today.getDate()),
    [today]
  );
  const defaultDate = useMemo(
    () =>
      new Date(today?.getFullYear() - 23, today.getMonth(), today.getDate()),
    [today]
  );

  const renderErrorMessage = (message: string): string => {
    const pattern = /Invalid identifier/;

    const emailPattern = /phone must match/;

    if (pattern?.test(message)) {
      return "Ai introdus greșit parola sau adresa de mail";
    } else if (emailPattern?.test(message)) {
      return "Nu ai introdus corect numărul tău de telefon.";
    }

    return "";
  };

  return (
    <WDataWrapper
      isLoading={updateProfileResponse?.loading}
      isError={updateProfileResponse?.error}
      isData
    >
      <div className="flex flex-col w-full gap-3">
        <motion.form
          initial={{ transform: "translateY(10px)", opacity: 0 }}
          animate={{ transform: "translateY(0px)", opacity: 1 }}
          exit={{ transform: "scale(0.8)", opacity: 0 }}
          onSubmit={handleSubmit(onSubmit)}
        >
          {!isProfileEdit && (
            <>
              <h1>{title ? title : t("complete-registration")}</h1>
              <div className="mt-5">
                <p>
                  {description
                    ? parse(description)
                    : t("complete-registration-description")}
                </p>
              </div>
              {showPassword && (
                <WTypography
                  text={t("existing-account")?.toString()}
                  className="font-bold text-ekgreen"
                />
              )}
            </>
          )}

          {/* separator */}
          <div className="mt-5 border" />

          {/* registration form */}
          <div className="flex flex-col gap-5 mt-5 lg:w-96">
            {title === undefined && (
              <span className="text-xl font-bold">
                {isProfileEdit ? tProfileEdit("tip-user") : t("logging-data")}
              </span>
            )}

            <div>
              <WTextInput
                {...register("email", {
                  pattern: {
                    value: /^[\w-.+]+@([\w-]+\.)+[\w-]{2,6}$/i,
                    message: "Aceasta nu este o adresă de e-mail validă!",
                  },
                  required: {
                    value: true,
                    message: "Introdu emailul tău",
                  },
                  onChange: (event) => {
                    setValue("email", event?.target?.value.replace(/\s/g, ""));
                    if (validateExistingEmail) {
                      validateEmail(event?.target?.value.replace(/\s/g, ""));
                      resetSpecificValues();
                    }
                  },
                })}
                onKeyDown={(event) => {
                  if (event?.code === "Space") {
                    event?.preventDefault();
                  }
                }}
                placeholder={t("email").toString()}
                required={true}
                defaultValue={profile?.email}
                disabled={!!profile}
                type="email"
                autoComplete="username"
                error={errors?.email}
              />

              {validateExistingEmail &&
                !isWebinarSignup &&
                userData?.usersPermissionsUsers?.data &&
                userData?.usersPermissionsUsers?.data?.length > 0 && (
                  <span className="w-full">
                    <span className="text-ekred">{t("existing-email")}.</span>{" "}
                    <span
                      className="cursor-pointer text-ekgreen"
                      onClick={() => router?.push(routes.user.login)}
                    >
                      {t("go-to-login")}
                    </span>
                  </span>
                )}
            </div>

            {showSpecificValues?.firstLastName && (
              <>
                <motion.div
                  className="flex flex-col w-full gap-4 lg:flex-row"
                  initial={{ transform: "translateY(10px)", opacity: 0 }}
                  animate={{ transform: "translateY(0px)", opacity: 1 }}
                  exit={{ transform: "scale(0.8)", opacity: 0 }}
                >
                  <div className="lg:w-1/2">
                    <WTextInput
                      {...register("firstName", {
                        required: true,
                        validate: (value) => !!value?.trim(),
                      })}
                      placeholder={t("firstName").toString()}
                      required={true}
                      defaultValue={profile?.firstName || ""}
                      error={errors?.firstName}
                    />
                  </div>

                  <div className="lg:w-1/2">
                    <WTextInput
                      {...register("lastName", {
                        required: true,
                        onChange: () =>
                          !hideInput && toggleFieldValue("password", true),
                        validate: (value) => !!value?.trim(),
                      })}
                      placeholder={t("lastName").toString()}
                      required={true}
                      defaultValue={profile?.lastName || ""}
                      error={errors?.lastName}
                    />
                  </div>
                </motion.div>
              </>
            )}

            {!withoutUsername && (
              <WTextInput
                {...register("username", { required: true })}
                placeholder={t("username").toString()}
                required={true}
                defaultValue={profile?.username}
                error={errors?.username}
              />
            )}

            {showPassword && (
              <>
                <WTextInput
                  {...register("password", {
                    required: {
                      value: true,
                      message: t("password-is-required"),
                    },
                    minLength: {
                      value: 6,
                      message: t("password-min-validation", {
                        value: 6,
                      }),
                    },
                    onChange: () =>
                      showSpecificValues?.firstLastName &&
                      toggleFieldValue("birthday", true),
                    validate: (value) => !!value?.trim(),
                  })}
                  placeholder={t("password").toString()}
                  required={true}
                  type="password"
                  autoComplete="current-password"
                  error={errors?.password}
                />
                <div className="text-center">
                  <span
                    onClick={() => router.push(routes.user.forgotPassword)}
                    className="text-green-btn"
                  >
                    {t("forgot-password")}
                  </span>
                </div>
              </>
            )}

            {!profile && showSpecificValues?.password && (
              <motion.div
                className="z-50"
                initial={{ transform: "translateY(10px)", opacity: 0 }}
                animate={{ transform: "translateY(0px)", opacity: 1 }}
                exit={{ transform: "scale(0.8)", opacity: 0 }}
              >
                <WTextInput
                  {...register("password", {
                    required: {
                      value: true,
                      message: t("password-is-required"),
                    },
                    minLength: {
                      value: 6,
                      message: t("password-min-validation", {
                        value: 6,
                      }),
                    },
                    onChange: () =>
                      showSpecificValues?.firstLastName &&
                      toggleFieldValue("birthday", true),
                    validate: (value) => !!value?.trim(),
                  })}
                  placeholder={t("password").toString()}
                  required={true}
                  type="password"
                  autoComplete="current-password"
                  error={errors?.password}
                />

                <WPasswordStrength passwordValue={watchPassword} />
                {/* <p className="text-sm">{t("password-description")}</p> */}
              </motion.div>
            )}

            {!isProfileEdit ||
              (title === undefined && (
                <>
                  <div className="mt-5 border" />

                  <span className="text-xl font-bold">
                    {t("personal-details")}
                  </span>
                </>
              ))}

            {showSpecificValues?.birthday && (
              <motion.div
                className="z-50 customDatePicker"
                initial={{ transform: "translateY(10px)", opacity: 0 }}
                animate={{ transform: "translateY(0px)", opacity: 1 }}
                exit={{ transform: "scale(1.0)", opacity: 0 }}
              >
                <Datepicker
                  startWeekOn="mon"
                  value={{
                    startDate: birthday || profile?.birthday,
                    endDate: birthday || profile?.birthday,
                  }}
                  inputClassName={clsx([
                    "!bg-white !text-black w-full rounded-md p-2 shadow-sm !h-14 ",
                    errors?.birthday && "!border !border-red-500",
                  ])}
                  readOnly
                  onChange={(event) => {
                    errors?.birthday && clearErrors("birthday");
                    if (event?.startDate) {
                      setValue("birthday", event?.startDate?.toString());
                      toggleFieldValue("city", true);
                      toggleFieldValue("checkbox", true);
                    }
                  }}
                  useRange={false}
                  maxDate={maxDate}
                  startFrom={defaultDate}
                  asSingle
                  placeholder={`${t("birthday")?.toString()}*`}
                  displayFormat="DD.MM.YYYY"
                />
              </motion.div>
            )}

            {showSpecificValues?.city && (
              <motion.div
                initial={{ transform: "translateY(10px)", opacity: 0 }}
                animate={{ transform: "translateY(0px)", opacity: 1 }}
                exit={{ transform: "scale(1.0)", opacity: 0 }}
              >
                <WTextInput
                  {...register("city", {
                    required: true,
                    onChange: () => {
                      toggleFieldValue("phone", true);
                      toggleFieldValue("submitButton", true);
                    },
                    validate: (value) => !!value?.trim(),
                  })}
                  placeholder={t("city").toString()}
                  required={true}
                  defaultValue={profile?.city || ""}
                  error={errors?.city}
                />
              </motion.div>
            )}

            {showSpecificValues?.phone && (
              <motion.div
                initial={{ transform: "translateY(10px)", opacity: 0 }}
                animate={{ transform: "translateY(0px)", opacity: 1 }}
                exit={{ transform: "scale(1.0)", opacity: 0 }}
                className="z-[1]"
              >
                <WPhoneInputWrapper
                  control={control as any}
                  name="phone"
                  errorMessage={errors?.phone}
                  required={true}
                />
              </motion.div>
            )}

            {showSpecificValues?.submitButton && (
              <WButton
                buttonType="submit"
                type="primary"
                disabled={
                  loading ||
                  !isValid ||
                  (!showPassword && showSpecificValues?.firstLastName)
                }
                text={
                  buttonText ??
                  (showPassword
                    ? t("login")
                    : profile
                      ? t("save")
                      : isWebinarSignup
                        ? t("connect-for-free")
                        : t("register-for-free"))
                }
              />
            )}

            <div className="text-center text-green-500">
              {loading ? `${t("loading")}...` : ""}
            </div>
            {error && !loading ? (
              <div className="text-center text-red-500">
                {renderErrorMessage(error?.message)}
              </div>
            ) : (
              ""
            )}

            {onCancel ? (
              <WButton
                buttonType="reset"
                type="secondary"
                text={
                  profile ? t("cancel") : t("cancel-registration").toUpperCase()
                }
                className="w-full"
                onClick={onCancel}
                disabled={!errors}
              />
            ) : (
              ""
            )}
          </div>
        </motion.form>
      </div>
    </WDataWrapper>
  );
};

export default WProfileEditPersonalDataForm;
